import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  SettingsCategory,
  useNavigationState,
} from "@/zustand/NavigationState";

/**
 * Opens a settings category middle-column view and clears the chatId from the
 * URL (flag `settingsMiddleViews`).
 *
 * Same contract as useStartSubscriptionView: the store action nulls
 * activeChatId but does NOT change the URL, so we navigate to `/` to keep the
 * URL and Zustand in sync — otherwise re-clicking the chat left in the URL is
 * a navigate() no-op and useChatRouteSync never restores it. The route-sync
 * reset guard (isMiddlePaneAction) is what lets the view survive this
 * navigation (docs/settings-middle-views/design.md, invariant 1).
 */
interface StartSettingsCategoryOptions {
  /**
   * Use history replace instead of push. Set true when called from the
   * deep-link page (/settings/:category) so pressing Back doesn't bounce the
   * user between the page and its redirect target.
   */
  replace?: boolean;
}

export function useStartSettingsCategoryView() {
  const startSettingsCategoryView = useNavigationState(
    (s) => s.startSettingsCategoryView,
  );
  const navigate = useNavigate();

  return useCallback(
    (category: SettingsCategory, options?: StartSettingsCategoryOptions) => {
      startSettingsCategoryView(category);
      if (options?.replace) {
        navigate("/", { replace: true });
      } else {
        navigate("/");
      }
    },
    [startSettingsCategoryView, navigate],
  );
}
